import { dev } from '$app/environment';
import * as Sentry from '@sentry/sveltekit';
import { setupNoRoutesFoundMonitoring } from '$lib/utils/sentryFilters';

Sentry.init({
  dsn: import.meta.env.VITE_SENTRY_DSN,
  integrations: [
    Sentry.browserTracingIntegration(),
    Sentry.replayIntegration({
      // Mask all text content, only capture clicks and navigation
      maskAllText: true,
      blockAllMedia: true,
    }),
  ],
  
  // Performance monitoring
  tracesSampleRate: dev ? 1.0 : 0.1, // 100% in dev, 10% in production
  
  // Session replay
  replaysSessionSampleRate: dev ? 1.0 : 0.01, // 100% in dev, 1% in production
  replaysOnErrorSampleRate: 1.0, // Always capture replays on errors
  
  // Development vs production environment
  environment: import.meta.env.VITE_ENVIRONMENT || (dev ? 'development' : 'production'),
  
  // Release information
  release: import.meta.env.VITE_SENTRY_RELEASE,
  
  // Enhanced error filtering and context
  beforeSend(event, hint) {
    // Filter out non-actionable errors
    const error = hint.originalException;
    const errorMessage = (error as any)?.message || (event as any).message || '';
    
    // Filter out network errors that are expected
    if (
      errorMessage.includes('NetworkError') ||
      errorMessage.includes('fetch') ||
      errorMessage.includes('AbortError') ||
      errorMessage.includes('The user aborted a request') ||
      errorMessage.includes('Load failed') ||
      errorMessage.includes('Script error')
    ) {
      return null;
    }
    
    // Filter out browser extension errors
    if (
      errorMessage.includes('extension') ||
      errorMessage.includes('chrome-extension') ||
      errorMessage.includes('moz-extension')
    ) {
      return null;
    }
    
    // Filter out wallet connection timeouts (expected behavior)
    if (errorMessage.includes('wallet') && errorMessage.includes('timeout')) {
      return null;
    }
    
    // Add additional context
    if (typeof window !== 'undefined') {
      event.contexts = {
        ...event.contexts,
        browser: {
          name: navigator.userAgent,
          online: navigator.onLine,
        },
        page: {
          url: window.location.href,
          referrer: document.referrer,
        }
      };
    }
    
    return event;
  },
  
  // Filter performance transactions
  beforeSendTransaction(event) {
    // Reduce noise from health checks and static assets
    if (
      event.transaction?.includes('/health') ||
      event.transaction?.includes('/favicon') ||
      event.transaction?.includes('/_app/') ||
      event.transaction?.endsWith('.css') ||
      event.transaction?.endsWith('.js') ||
      event.transaction?.endsWith('.png') ||
      event.transaction?.endsWith('.svg')
    ) {
      return null;
    }
    
    return event;
  },
  
  // Breadcrumb filtering
  beforeBreadcrumb(breadcrumb) {
    // Filter out console logs in production
    if (!dev && breadcrumb.category === 'console') {
      return null;
    }
    
    // Limit URL breadcrumbs to important navigation
    if (breadcrumb.category === 'navigation') {
      // Keep important navigation events
      return breadcrumb;
    }
    
    // Filter out noisy click events
    if (breadcrumb.category === 'ui.click') {
      const target = breadcrumb.data?.['ui.target'];
      if (target?.includes('button') || target?.includes('link')) {
        return breadcrumb;
      }
      return null;
    }
    
    return breadcrumb;
  },
  
  // Enhanced settings
  maxBreadcrumbs: 75,
  sendDefaultPii: false,
  
  // Additional integrations
  initialScope: {
    tags: {
      component: 'client',
      feature_branch: import.meta.env.VITE_CF_PAGES_BRANCH || 'unknown'
    },
  },
});

// Set up custom monitoring for No Routes Found issues
setupNoRoutesFoundMonitoring(); 